package com.eladfinish.bible.contest.screens.search.usecases.date

/**
 * A date to search for. Months are counted from Nisan, as in the Bible: 1 = Nisan … 7 = Tishrei …
 * 12 = Adar. [day] == null means the whole month.
 */
data class DateQuery(
    val month: Int,
    val day: Int?,
) {
    /** "י׳ בתשרי (החודש השביעי)" / "חודש תשרי (החודש השביעי)" — how the query was understood. */
    val label: String
        get() = "${shortLabel()} (החודש ${MONTH_ORDINALS[month - 1]})"

    /** "י׳ בתשרי" / "חודש תשרי". */
    fun shortLabel(): String =
        if (day == null) "חודש ${MONTH_NAMES[month - 1]}" else dayInMonthLabel(month, day)

    companion object {
        val MONTH_NAMES = listOf(
            "ניסן", "אייר", "סיון", "תמוז", "אב", "אלול",
            "תשרי", "חשוון", "כסלו", "טבת", "שבט", "אדר",
        )

        val MONTH_ORDINALS = listOf(
            "הראשון", "השני", "השלישי", "הרביעי", "החמישי", "השישי",
            "השביעי", "השמיני", "התשיעי", "העשירי", "האחד עשר", "השנים עשר",
        )

        /** "י״ג באדר". */
        fun dayInMonthLabel(month: Int, day: Int): String =
            "${dayWithGershaim(day)} ב${MONTH_NAMES[month - 1]}"

        /** 1..30 as Hebrew letters with geresh / gershayim: א׳, י״ד, ט״ו, ל׳. */
        fun dayWithGershaim(day: Int): String {
            val letters = dayToLetters(day)
            return if (letters.length == 1) "$letters׳" else "${letters.dropLast(1)}״${letters.last()}"
        }

        /** 1..30 as plain Hebrew letters (15 = טו, 16 = טז). */
        fun dayToLetters(day: Int): String {
            require(day in 1..30) { "day $day out of range" }
            return when (day) {
                15 -> "טו"
                16 -> "טז"
                else -> TENS[day / 10] + UNITS[day % 10]
            }
        }

        private val UNITS = listOf("", "א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט")
        private val TENS = listOf("", "י", "כ", "ל")
    }
}
